// Procedural Web Audio — seismic-like lunar pulses + spatial fly swells + ambient beds.
class EclipseAudio {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.ambientGain = null;  // dedicated gain for the ambient bed (user volume dial)
    this.ambientVol = 0.7;
    this.muted = false;
    this.ambient = null;      // active ambient node graph
    this.ambientKind = null;  // "space" | "ground"
  }
  _ensure() {
    if (this.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.6;
    this.master.connect(this.ctx.destination);
    this.ambientGain = this.ctx.createGain();
    this.ambientGain.gain.value = this.ambientVol;
    this.ambientGain.connect(this.master);
  }
  resume() {
    this._ensure();
    if (this.ctx && this.ctx.state === "suspended") this.ctx.resume();
  }
  setMuted(m) {
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : 0.6;
  }
  setAmbientVolume(v) {
    this.ambientVol = v;
    if (this.ambientGain && this.ctx) {
      this.ambientGain.gain.setTargetAtTime(v, this.ctx.currentTime, 0.05);
    }
  }
  // deep seismic pulse — a decaying low sine with a sub-harmonic
  pulse(freq = 46, intensity = 1) {
    this._ensure();
    if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const o2 = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "sine"; o2.type = "triangle";
    o.frequency.setValueAtTime(freq, t);
    o.frequency.exponentialRampToValueAtTime(freq * 0.6, t + 1.6);
    o2.frequency.setValueAtTime(freq * 1.5, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.5 * intensity, t + 0.06);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
    o.connect(g); o2.connect(g); g.connect(this.master);
    o.start(t); o2.start(t);
    o.stop(t + 1.9); o2.stop(t + 1.9);
  }
  // atmospheric swell used when flying between macro/micro perspectives
  swell() {
    this._ensure();
    if (!this.ctx || this.muted) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    const f = this.ctx.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.setValueAtTime(180, t);
    f.frequency.exponentialRampToValueAtTime(1400, t + 0.9);
    f.frequency.exponentialRampToValueAtTime(220, t + 1.8);
    o.type = "sawtooth";
    o.frequency.setValueAtTime(90, t);
    o.frequency.exponentialRampToValueAtTime(150, t + 0.9);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.4, t + 0.5);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.9);
    o.connect(f); f.connect(g); g.connect(this.master);
    o.start(t); o.stop(t + 2);
  }
  // continuous ambient bed — a deep-space drone or a low city hum. Muteable via master.
  startAmbient(kind = "space") {
    this._ensure();
    if (!this.ctx) return;
    if (this.ambientKind === kind && this.ambient) return;
    this.stopAmbient();
    const t = this.ctx.currentTime;
    const bus = this.ctx.createGain();
    bus.gain.setValueAtTime(0.0001, t);
    bus.gain.exponentialRampToValueAtTime(kind === "ground" ? 0.14 : 0.1, t + 2.5);
    bus.connect(this.ambientGain || this.master);

    const nodes = [bus];
    if (kind === "space") {
      // two detuned low sines + slow shimmering LFO on a lowpass
      const freqs = [42, 63, 84.5];
      const filt = this.ctx.createBiquadFilter();
      filt.type = "lowpass"; filt.frequency.value = 320; filt.Q.value = 0.6;
      filt.connect(bus);
      const lfo = this.ctx.createOscillator();
      const lfoGain = this.ctx.createGain();
      lfo.frequency.value = 0.07; lfoGain.gain.value = 140;
      lfo.connect(lfoGain); lfoGain.connect(filt.frequency);
      lfo.start(t); nodes.push(lfo, lfoGain, filt);
      freqs.forEach((f, i) => {
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.type = "sine"; o.frequency.value = f;
        g.gain.value = i === 0 ? 0.5 : 0.28;
        o.connect(g); g.connect(filt); o.start(t);
        nodes.push(o, g);
      });
    } else {
      // ground: warmer detuned saw hum + gentle noise wash
      const filt = this.ctx.createBiquadFilter();
      filt.type = "lowpass"; filt.frequency.value = 620; filt.Q.value = 0.4;
      filt.connect(bus);
      [70, 70.6, 105].forEach((f, i) => {
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.type = i === 2 ? "triangle" : "sawtooth"; o.frequency.value = f;
        g.gain.value = i === 2 ? 0.18 : 0.22;
        o.connect(g); g.connect(filt); o.start(t);
        nodes.push(o, g);
      });
      // soft filtered noise for city air
      const bufSize = 2 * this.ctx.sampleRate;
      const buf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
      const noise = this.ctx.createBufferSource();
      noise.buffer = buf; noise.loop = true;
      const nf = this.ctx.createBiquadFilter();
      nf.type = "bandpass"; nf.frequency.value = 480; nf.Q.value = 0.7;
      const ng = this.ctx.createGain(); ng.gain.value = 0.05;
      noise.connect(nf); nf.connect(ng); ng.connect(bus); noise.start(t);
      nodes.push(noise, nf, ng, filt);
    }
    this.ambient = { bus, nodes };
    this.ambientKind = kind;
  }
  stopAmbient() {
    if (!this.ambient || !this.ctx) { this.ambient = null; this.ambientKind = null; return; }
    const t = this.ctx.currentTime;
    const { bus, nodes } = this.ambient;
    try { bus.gain.cancelScheduledValues(t); bus.gain.setValueAtTime(bus.gain.value, t); bus.gain.exponentialRampToValueAtTime(0.0001, t + 0.8); } catch (e) {}
    nodes.forEach((n) => { try { if (n.stop) n.stop(t + 1.0); } catch (e) {} });
    this.ambient = null;
    this.ambientKind = null;
  }
}
export const audio = new EclipseAudio();
